import React, { useState } from 'react';
import { db } from '../firebase';
import { doc, setDoc } from 'firebase/firestore';
import { useTheme } from '../ThemeContext';

const F = "'Fira Code', monospace";

const Liabilities = ({ data, onUpdate, user }) => {
  const { T } = useTheme();

  // Cards State
  const [cardName, setCardName] = useState('');
  const [cardLimit, setCardLimit] = useState('');
  const [billDate, setBillDate] = useState('');
  
  const [spendAmount, setSpendAmount] = useState('');
  const [spendNote, setSpendNote] = useState('');
  const [selectedCard, setSelectedCard] = useState('');
  
  const [payAmount, setPayAmount] = useState('');
  const [payCard, setPayCard] = useState('');
  
  // Loans State
  const [loanName, setLoanName] = useState('');
  const [loanPrincipal, setLoanPrincipal] = useState('');
  const [emiAmount, setEmiAmount] = useState('');
  const [emiDate, setEmiDate] = useState('');

  const [saving, setSaving] = useState(false);
  const [flash, setFlash] = useState('');

  const showFlash = (msg) => { setFlash(msg); setTimeout(() => setFlash(''), 2500); };

  const cards = data.cards || [];
  const loans = data.loans || [];

  // ─── ADD NEW CARD ───
  const handleAddCard = async () => {
    if (!cardName.trim() || !cardLimit || !billDate) return showFlash('✗ FILL ALL CARD DETAILS');
    setSaving(true);
    const newCard = {
      id: Date.now().toString(),
      name: cardName.trim(),
      limit: parseFloat(cardLimit),
      used: 0,
      billDate: parseInt(billDate),
      transactions: []
    };
    const newCards = [...cards, newCard];
    try {
      await setDoc(doc(db, 'users', user.uid, 'data', 'liabilities'), { cards: newCards }, { merge: true });
      onUpdate({ cards: newCards });
      setCardName(''); setCardLimit(''); setBillDate('');
      showFlash('✓ CREDIT CARD ADDED');
    } catch (e) { showFlash('✗ WRITE FAILED'); }
    setSaving(false);
  };

  // ─── LOG CARD EXPENSE ───
  const handleCardSpend = async () => {
    if (!selectedCard || !spendAmount || parseFloat(spendAmount) <= 0) return showFlash('✗ INVALID SPEND');
    setSaving(true);
    const amt = parseFloat(spendAmount);
    
    const newCards = cards.map(c => {
      if (c.id === selectedCard) {
        return {
          ...c,
          used: c.used + amt,
          transactions: [{ type: 'SPEND', amount: amt, note: spendNote, date: new Date().toISOString() }, ...(c.transactions || [])]
        };
      }
      return c;
    });

    try {
      await setDoc(doc(db, 'users', user.uid, 'data', 'liabilities'), { cards: newCards }, { merge: true });
      onUpdate({ cards: newCards });
      setSpendAmount(''); setSpendNote('');
      showFlash('✓ CARD EXPENSE LOGGED');
    } catch (e) { showFlash('✗ WRITE FAILED'); }
    setSaving(false);
  };

  // ─── PAY CARD BILL ───
  const handlePayCard = async () => {
    if (!payCard || !payAmount || parseFloat(payAmount) <= 0) return showFlash('✗ INVALID PAYMENT');
    setSaving(true);
    const amt = parseFloat(payAmount);

    const newCards = cards.map(c => {
      if (c.id === payCard) {
        return {
          ...c,
          used: Math.max(0, c.used - amt),
          transactions: [{ type: 'PAYMENT', amount: amt, note: 'Bill Paid', date: new Date().toISOString() }, ...(c.transactions || [])]
        };
      }
      return c;
    });
    
    // Deduct payment from Salary
    const newBal = (data.salaryBalance || 0) - amt;

    try {
      await setDoc(doc(db, 'users', user.uid, 'data', 'liabilities'), { cards: newCards }, { merge: true });
      await setDoc(doc(db, 'users', user.uid, 'data', 'salary'), { currentBalance: newBal }, { merge: true });
      onUpdate({ cards: newCards, salaryBalance: newBal });
      setPayAmount('');
      showFlash('✓ BILL PAID & DEDUCTED FROM SALARY');
    } catch (e) { showFlash('✗ WRITE FAILED'); }
    setSaving(false);
  };

  // ─── ADD NEW LOAN ───
  const handleAddLoan = async () => {
    if (!loanName.trim() || !loanPrincipal || !emiAmount || !emiDate) return showFlash('✗ FILL ALL LOAN DETAILS');
    setSaving(true);
    const newLoan = {
      id: Date.now().toString(),
      name: loanName.trim(),
      principal: parseFloat(loanPrincipal),
      emi: parseFloat(emiAmount),
      emiDate: parseInt(emiDate),
      paid: 0
    };
    const newLoans = [...loans, newLoan];
    try {
      await setDoc(doc(db, 'users', user.uid, 'data', 'liabilities'), { loans: newLoans }, { merge: true });
      onUpdate({ loans: newLoans });
      setLoanName(''); setLoanPrincipal(''); setEmiAmount(''); setEmiDate('');
      showFlash('✓ LOAN CREATED');
    } catch (e) { showFlash('✗ WRITE FAILED'); }
    setSaving(false);
  };

  // ─── PAY EMI ───
  const handlePayEmi = async (loanId, emiVal) => {
    setSaving(true);
    const newLoans = loans.map(l => {
      if (l.id === loanId) {
        return { ...l, principal: Math.max(0, l.principal - emiVal), paid: l.paid + emiVal };
      }
      return l;
    });

    const newBal = (data.salaryBalance || 0) - emiVal;

    try {
      await setDoc(doc(db, 'users', user.uid, 'data', 'liabilities'), { loans: newLoans }, { merge: true });
      await setDoc(doc(db, 'users', user.uid, 'data', 'salary'), { currentBalance: newBal }, { merge: true });
      onUpdate({ loans: newLoans, salaryBalance: newBal });
      showFlash('✓ EMI PAID & DEDUCTED FROM SALARY');
    } catch (e) { showFlash('✗ WRITE FAILED'); }
    setSaving(false);
  };

  const totalCardDebt = cards.reduce((s, c) => s + c.used, 0);
  const totalLoanDebt = loans.reduce((s, l) => s + l.principal, 0);
  const activeDebt = totalCardDebt + totalLoanDebt;

  const cardS = { background: T.card, border: `1px solid ${T.border}`, borderRadius: 10, padding: '22px 22px', marginBottom: 14, boxShadow: T.isDark ? 'none' : '0 2px 12px rgba(0,0,0,0.06)' };
  const hdrS  = { fontSize: 10, letterSpacing: '0.25em', color: T.text, fontFamily: F, marginBottom: 18, fontWeight: 700, paddingBottom: 10, borderBottom: `1px solid ${T.border}` };

  return (
    <div style={{ maxWidth: 780, margin: '0 auto' }}>
      {flash && <Flash msg={flash} T={T} />}

      {/* Overview Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
        <SC label="TOTAL LIABILITIES" value={`₹${activeDebt.toLocaleString('en-IN')}`} color={T.rose} T={T} />
        <SC label="CREDIT CARD OUTSTANDING" value={`₹${totalCardDebt.toLocaleString('en-IN')}`} color={T.amber} T={T} />
        <SC label="LOAN PRINCIPAL LEFT" value={`₹${totalLoanDebt.toLocaleString('en-IN')}`} color={T.violet} T={T} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: 14 }}>
        {/* CARDS UI */}
        <div>
          <div style={cardS}>
             <div style={hdrS}>💳 CREDIT CARDS</div>
             {cards.length === 0 ? (
               <div style={{ fontSize: 10, color: T.dim, textAlign: 'center', padding: '20px 0' }}>NO CARDS FOUND</div>
             ) : (
               cards.map(c => {
                 const pct = Math.min((c.used / c.limit) * 100, 100);
                 const cColor = pct > 80 ? T.rose : (pct > 30 ? T.amber : T.emerald);
                 return (
                   <div key={c.id} style={{ marginBottom: 16, border: `1px solid ${T.border}`, padding: '12px 14px', borderRadius: 8, background: T.isDark?'rgba(255,255,255,0.02)':'#fafafa' }}>
                     <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                       <div style={{ fontSize: 13, fontWeight: 900, color: T.text }}>{c.name.toUpperCase()}</div>
                       <div style={{ fontSize: 8, color: T.slate, border:`1px solid ${T.border}`, padding: '3px 6px', borderRadius: 4 }}>BILL: {c.billDate}th</div>
                     </div>
                     <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, marginBottom: 6 }}>
                       <span style={{ color: cColor, fontWeight: 700 }}>₹{c.used.toLocaleString('en-IN')} OUTSTANDING</span>
                       <span style={{ color: T.dim }}>OF ₹{(c.limit/1000).toFixed(0)}k LIMIT</span>
                     </div>
                     <div style={{ width: '100%', height: 4, background: T.muted, borderRadius: 2 }}>
                       <div style={{ width: `${pct}%`, height: '100%', background: cColor, borderRadius: 2 }} />
                     </div>
                   </div>
                 );
               })
             )}
          </div>
          
          {cards.length > 0 && (
            <>
              <div style={cardS}>
                <div style={hdrS}>📉 LOG CARD EXPENSE</div>
                <select value={selectedCard} onChange={e=>setSelectedCard(e.target.value)} style={{ width: '100%', background: T.inputBg, border: 'none', borderBottom: `1.5px solid ${T.inputBdr}`, padding: '10px 8px', color: T.text, outline: 'none', fontFamily: F, marginBottom: 12 }}>
                  <option value="" disabled>SELECT CARD</option>
                  {cards.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
                  <IB label="AMOUNT" type="number" value={spendAmount} onChange={setSpendAmount} T={T} />
                  <IB label="NOTE" type="text" value={spendNote} onChange={setSpendNote} T={T} />
                </div>
                <BtnX onClick={handleCardSpend} disabled={saving} label="LOG SPEND" color={T.amber} T={T} />
              </div>

              <div style={cardS}>
                <div style={hdrS}>💸 PAY CARD BILL</div>
                <div style={{ fontSize: 8, color: T.slate, marginBottom: 10, letterSpacing: '0.1em' }}>(Deducts from Salary Balance)</div>
                <select value={payCard} onChange={e=>setPayCard(e.target.value)} style={{ width: '100%', background: T.inputBg, border: 'none', borderBottom: `1.5px solid ${T.inputBdr}`, padding: '10px 8px', color: T.text, outline: 'none', fontFamily: F, marginBottom: 12 }}>
                  <option value="" disabled>SELECT CARD</option>
                  {cards.map(c => <option key={c.id} value={c.id}>{c.name} — Outstanding: ₹{c.used}</option>)}
                </select>
                <div style={{ marginBottom: 14 }}>
                  <IB label="PAYMENT AMOUNT" type="number" value={payAmount} onChange={setPayAmount} T={T} />
                </div>
                <BtnX onClick={handlePayCard} disabled={saving} label="PAY BILL" color={T.emerald} T={T} />
              </div>
            </>
          )}

          <div style={cardS}>
             <div style={hdrS}>+ ADD NEW CREDIT CARD</div>
             <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                <IB label="CARD NAME" type="text" value={cardName} onChange={setCardName} placeholder="SBI Cashback" T={T} />
                <IB label="CREDIT LIMIT" type="number" value={cardLimit} onChange={setCardLimit} T={T} />
             </div>
             <div style={{ marginBottom: 14 }}>
                <IB label="BILLING DATE (1-31)" type="number" value={billDate} onChange={setBillDate} T={T} />
             </div>
             <BtnX onClick={handleAddCard} disabled={saving} label="ADD CARD" color={T.sky} T={T} />
          </div>
        </div>

        {/* LOANS UI */}
        <div>
          <div style={cardS}>
            <div style={hdrS}>🏦 ACTIVE LOANS & EMIs</div>
            {loans.length === 0 ? (
               <div style={{ fontSize: 10, color: T.dim, textAlign: 'center', padding: '20px 0' }}>NO ACTIVE LOANS</div>
             ) : (
               loans.map(l => (
                 <div key={l.id} style={{ marginBottom: 16, border: `1px solid ${T.border}`, padding: '14px', borderRadius: 8, background: T.isDark?'rgba(255,255,255,0.02)':'#fafafa' }}>
                   <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                     <div style={{ fontSize: 13, fontWeight: 900, color: T.text }}>{l.name.toUpperCase()}</div>
                     <div style={{ fontSize: 9, color: T.rose, fontWeight: 700 }}>EMI: ₹{l.emi.toLocaleString('en-IN')}</div>
                   </div>
                   <div style={{ fontSize: 10, color: T.dim, marginBottom: 12 }}>
                     Left: <span style={{ color: T.violet, fontWeight: 700 }}>₹{l.principal.toLocaleString('en-IN')}</span>
                     <span style={{ margin: '0 8px' }}>|</span> Total Paid: ₹{l.paid.toLocaleString('en-IN')}
                   </div>
                   <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: T.isDark?'rgba(0,0,0,0.2)':'rgba(0,0,0,0.03)', padding: '8px 10px', borderRadius: 6 }}>
                     <div style={{ fontSize: 8, color: T.slate }}>DUE ON: <span style={{ color: T.text, fontWeight: 700 }}>{l.emiDate}</span> OF MONTH</div>
                     <button onClick={() => handlePayEmi(l.id, l.emi)} style={{ background: T.emerald, color: '#fff', border: 'none', padding: '4px 10px', borderRadius: 4, fontFamily: F, fontSize: 8, fontWeight: 900, cursor: 'pointer' }}>PAY EMI</button>
                   </div>
                 </div>
               ))
             )}
          </div>

          <div style={cardS}>
            <div style={hdrS}>+ REGISTER NEW LOAN</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 10, marginBottom: 14 }}>
               <IB label="LOAN TYPE/NAME" type="text" value={loanName} onChange={setLoanName} placeholder="Car Loan" T={T} />
               <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                 <IB label="PRINCIPAL LEFT" type="number" value={loanPrincipal} onChange={setLoanPrincipal} T={T} />
                 <IB label="MONTHLY EMI" type="number" value={emiAmount} onChange={setEmiAmount} T={T} />
               </div>
               <IB label="EMI DATE (1-31)" type="number" value={emiDate} onChange={setEmiDate} T={T} />
            </div>
            <BtnX onClick={handleAddLoan} disabled={saving} label="ADD LOAN" color={T.violet} T={T} />
          </div>
        </div>
      </div>
    </div>
  );
};

/* Shared */
const Flash = ({ msg, T }) => (
  <div style={{ position: 'fixed', top: 20, left: '50%', transform: 'translateX(-50%)', zIndex: 999, background: msg.startsWith('✓') ? `${T.emerald}18` : `${T.rose}18`, border: `1px solid ${msg.startsWith('✓') ? T.emerald : T.rose}`, color: msg.startsWith('✓') ? T.emerald : T.rose, padding: '10px 28px', borderRadius: 6, fontSize: 11, letterSpacing: '0.2em', fontFamily: F, animation: 'flashIn 0.3s ease' }}>
  {msg}<style>{`@keyframes flashIn { from{opacity:0;transform:translateX(-50%) translateY(-8px)} to{opacity:1;transform:translateX(-50%) translateY(0)} }`}</style>
  </div>
);

const SC = ({ label, value, color, T }) => (
  <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 10, padding: '18px 16px', textAlign: 'center', position: 'relative', overflow: 'hidden', boxShadow: T.isDark ? 'none' : '0 2px 10px rgba(0,0,0,0.05)', transition: 'background .3s' }}>
    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: color, opacity: 0.5 }} />
    <div style={{ fontSize: 7, letterSpacing: '0.35em', color: T.dim, fontFamily: F, marginBottom: 10 }}>{label}</div>
    <div style={{ fontSize: 18, fontWeight: 900, color, fontFamily: F }}>{value}</div>
  </div>
);

const IB = ({ label, type, value, onChange, placeholder, T }) => (
  <div>
    <label style={{ fontSize: 8, letterSpacing: '0.35em', color: T.dim, fontFamily: F, marginBottom: 6, display: 'block' }}>&gt; {label}</label>
    <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder || '0'}
      style={{ width: '100%', background: T.inputBg, border: 'none', borderBottom: `1.5px solid ${T.inputBdr}`, padding: '10px 8px', color: T.text, fontSize: 14, letterSpacing: '0.1em', outline: 'none', fontFamily: F, caretColor: T.inputFocus }}
      onFocus={e => e.target.style.borderBottomColor = T.inputFocus} onBlur={e => e.target.style.borderBottomColor = T.inputBdr} />
  </div>
);

const BtnX = ({ onClick, disabled, label, color, T }) => (
  <button onClick={onClick} disabled={disabled}
    style={{ width: '100%', background: 'transparent', border: `1px solid ${color}44`, color, padding: '12px 0', fontSize: 10, letterSpacing: '0.5em', fontWeight: 700, cursor: 'pointer', fontFamily: F, borderRadius: 6, transition: 'all 0.25s' }}
    onMouseOver={e => { e.currentTarget.style.background = color + '15'; e.currentTarget.style.borderColor = color; }}
    onMouseOut={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = color + '44'; }}
  >{label}</button>
);

export default Liabilities;
